import type { ApisInfo as ApiApisInfo } from '@models/landing/api/api-response.model';
import type { ApisInfo as UiApisInfo } from '@models/ui/pnr-data-model';
import { formatDateToYYYYMMDD } from '@utils/date-time.util';

/**
 * Helper to safely convert number to string or return empty string
 */
const toStringOrEmpty = (value: number | null): string => (value !== null ? String(value) : '');

/**
 * Helper to return value or empty string
 */
const orEmpty = (value: string | null): string => value ?? '';

/**
 * Maps passenger basic information fields
 */
const mapPassengerInfo = (
  api: ApiApisInfo,
): Pick<UiApisInfo, 'firstName' | 'middleName' | 'lastName' | 'gender' | 'dateOfBirth'> => ({
  firstName: orEmpty(api.FNM),
  middleName: orEmpty(api.MNM),
  lastName: orEmpty(api.LNM),
  gender: orEmpty(api.GEN),
  dateOfBirth: formatDateToYYYYMMDD(api.DOB),
});

/**
 * Maps document/passport information fields
 */
const mapDocumentInfo = (
  api: ApiApisInfo,
): Pick<
  UiApisInfo,
  | 'documentType'
  | 'documentSubType'
  | 'documentNumber'
  | 'documentExpiry'
  | 'documentIssueDate'
  | 'placeOfIssue'
  | 'issuingCountry'
  | 'stateOfIssue'
> => ({
  documentType: toStringOrEmpty(api.DOT),
  documentSubType: toStringOrEmpty(api.DOS),
  documentNumber: orEmpty(api.DON),
  documentExpiry: formatDateToYYYYMMDD(api.DOE),
  documentIssueDate: formatDateToYYYYMMDD(api.DOI),
  placeOfIssue: orEmpty(api.POI),
  issuingCountry: orEmpty(api.COI),
  stateOfIssue: orEmpty(api.SOI),
});

/**
 * Maps nationality and residence fields
 */
const mapNationalityInfo = (
  api: ApiApisInfo,
): Pick<UiApisInfo, 'nationality' | 'countryOfResidence'> => ({
  nationality: orEmpty(api.NAT),
  countryOfResidence: orEmpty(api.COR),
});

/**
 * Maps birth information fields
 */
const mapBirthInfo = (
  api: ApiApisInfo,
): Pick<UiApisInfo, 'placeOfBirth' | 'countryOfBirth' | 'stateOfBirth'> => ({
  placeOfBirth: orEmpty(api.POB),
  countryOfBirth: orEmpty(api.COB),
  stateOfBirth: orEmpty(api.SOB),
});

/**
 * Maps visa information fields
 */
const mapVisaInfo = (
  api: ApiApisInfo,
): Pick<
  UiApisInfo,
  | 'visaType'
  | 'visaNumber'
  | 'visaIssueDate'
  | 'visaExpiry'
  | 'visaPlaceOfIssue'
  | 'visaCountryOfIssue'
  | 'visaStateOfIssue'
> => ({
  visaType: toStringOrEmpty(api.VIT),
  visaNumber: orEmpty(api.VIN),
  visaIssueDate: formatDateToYYYYMMDD(api.VDI),
  visaExpiry: formatDateToYYYYMMDD(api.VDE),
  visaPlaceOfIssue: orEmpty(api.VPI),
  visaCountryOfIssue: orEmpty(api.VCI),
  visaStateOfIssue: orEmpty(api.VSI),
});

/**
 * Maps permanent resident information fields
 */
const mapPermanentResidentInfo = (
  api: ApiApisInfo,
): Pick<
  UiApisInfo,
  | 'permanentResidentNumber'
  | 'prExpiry'
  | 'prIssueDate'
  | 'prPlaceOfIssue'
  | 'prCountry'
  | 'prStateOfIssue'
> => ({
  permanentResidentNumber: orEmpty(api.PRN),
  prExpiry: formatDateToYYYYMMDD(api.PDE),
  prIssueDate: formatDateToYYYYMMDD(api.PDI),
  prPlaceOfIssue: orEmpty(api.PPI),
  prCountry: orEmpty(api.PCI),
  prStateOfIssue: orEmpty(api.PSI),
});

/**
 * Maps home address fields
 */
const mapHomeAddress = (
  api: ApiApisInfo,
): Pick<UiApisInfo, 'homeAddress' | 'homeCity' | 'homeState' | 'homeZipCode' | 'homeCountry'> => ({
  homeAddress: orEmpty(api.HAD),
  homeCity: orEmpty(api.HCT),
  homeState: orEmpty(api.HST),
  homeZipCode: orEmpty(api.HZP),
  homeCountry: orEmpty(api.HCO),
});

/**
 * Maps destination address fields
 */
const mapDestinationAddress = (
  api: ApiApisInfo,
): Pick<
  UiApisInfo,
  | 'destinationAddress'
  | 'destinationCity'
  | 'destinationState'
  | 'destinationZipCode'
  | 'destinationCountry'
  | 'destinationContactNumber'
> => ({
  destinationAddress: orEmpty(api.DAD),
  destinationCity: orEmpty(api.DCT),
  destinationState: orEmpty(api.DST),
  destinationZipCode: orEmpty(api.DZP),
  destinationCountry: orEmpty(api.DCO),
  destinationContactNumber: orEmpty(api.DNO),
});

/**
 * Maps API APIS info (abbreviated field names) to UI APIS info (descriptive names)
 * Complete mapping of all 43 APIS fields used in passenger information.
 *
 * Field Mapping Reference:
 * - Passenger: FNM/MNM/LNM → firstName/middleName/lastName, GEN → gender, DOB → dateOfBirth
 * - Document: DOT/DOS/DON/DOE/DOI → documentType/SubType/Number/Expiry/IssueDate, POI/COI/SOI → placeOf/countryOf/stateOfIssue
 * - Nationality: NAT → nationality, COR → countryOfResidence
 * - Birth: POB/COB/SOB → placeOf/countryOf/stateOfBirth
 * - Visa: VIT/VIN/VDI/VDE → visaType/Number/IssueDate/Expiry, VPI/VCI/VSI → visaPlaceOf/CountryOf/StateOfIssue
 * - PR: PRN/PDE/PDI/PPI/PCI/PSI → prNumber/Expiry/IssueDate/PlaceOfIssue/Country/StateOfIssue
 * - Home: HAD/HCT/HST/HZP/HCO → homeAddress/City/State/ZipCode/Country
 * - Destination: DAD/DCT/DST/DZP/DCO/DNO → destinationAddress/City/State/ZipCode/Country/ContactNumber
 * - Other: RNO → redressNumber
 *
 * @param apiApisInfo - APIS data from API with abbreviated field names
 * @returns Mapped APIS data with descriptive field names for UI consumption
 */
export const mapApiApisInfoToUi = (apiApisInfo?: ApiApisInfo | null): UiApisInfo | undefined => {
  if (!apiApisInfo) {
    return undefined;
  }

  return {
    ...mapPassengerInfo(apiApisInfo),
    ...mapDocumentInfo(apiApisInfo),
    ...mapNationalityInfo(apiApisInfo),
    ...mapBirthInfo(apiApisInfo),
    ...mapVisaInfo(apiApisInfo),
    ...mapPermanentResidentInfo(apiApisInfo),
    ...mapHomeAddress(apiApisInfo),
    ...mapDestinationAddress(apiApisInfo),
    redressNumber: orEmpty(apiApisInfo.RNO),
  };
};
